import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/sport-gear-storage.jpg'),
    alt: 'sport gear storage page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'sport gear storage page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Sport Gear <span>Storage</span> <span>The Best Solution for Athletes</span>',
  description:
    'Discover ShipAndStorage’s tailored services for Sport Gear Storage, offering convenience, security, and accessibility for your sports equipment, all year round.',
  image: {
    url: '/images/v2/pages/main-pages/sport-gear-storage/hero-image.jpg',
    alt: 'sport gear storage page'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Store Your Sport Gears' }
}

export const whyWeSectionContent = {
  title: 'How to Store Your Sports Gear with ShipAndStorage',
  description:
    'Storing your sports gear is simple and efficient with our streamlined process. Follow these four steps:',

  cards: [
    {
      id: 1,
      title: 'Create an Account',
      description: 'Sign up on our platform and access tailored Sport Gear Storage solutions.',
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/create-an-account.svg',
        alt: 'Create an Account'
      }
    },
    {
      id: 2,
      title: 'Prepare Your Equipment',
      description: 'Clean and pack your gear securely to ensure its protection during transit.',
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/prepare-your-equipment.svg',
        alt: 'Prepare Your Equipment'
      }
    },
    {
      id: 3,
      title: 'Schedule Pickup or Drop-Off',
      description: 'Choose the most convenient option to get your gear to our facility.',
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/schedule-pickup-or-drop-off.svg',
        alt: 'Schedule Pickup or Drop-Off service'
      }
    },
    {
      id: 4,
      title: 'Relax and Access Anytime',
      description: 'We store your equipment safely, and you can retrieve it when needed.',
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/relax-and-access-anytime.svg',
        alt: 'Relax and Access Anytime'
      }
    }
  ]
}

export const helperSectionContent = {
  title: 'Convenient and Secure Sport Gear Storage',

  articles: [
    {
      link: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Learn More About Our Services' },
      content: [
        {
          id: 1,
          description:
            'Convenient and secure sports gear storage ensures your equipment is always protected and ready for use. Whether you need to store skis, snowboards, or golf clubs during the off-season or temporarily free up space at home, our service provides a hassle-free solution.'
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/convenient-and-secure-sport-gear-storage.jpg',
        alt: 'Convenient and Secure Sport Gear Storage'
      }
    }
  ]
}

export const whyProperSectionContent = {
  title: 'Why Proper Sport Gear Storage Matters',
  description:
    'Storing sports gear effectively is essential for maintaining its condition and ensuring it’s ready for use when you need it. Here are some key reasons to prioritize proper storage:',

  articles: [
    {
      link: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Get Your Storage' },

      content: [
        {
          id: 1,
          points: [
            '<strong>Preserve Equipment Longevity:</strong> Prevent wear and tear caused by improper handling or environmental factors.',
            '<Strong>Organize Your Space:</strong> Declutter your home or garage by keeping bulky sports gear in a secure, dedicated space.',
            '<strong>Seasonal Convenience:</strong> Store seasonal items like skis, snowboards, or surfboards safely during the off-season.',
            '<strong>Easy Access:</strong> Ensure your gear is ready to go without the hassle of searching through cluttered spaces.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/why-proper-sport-gear-storage-matters.svg',
        alt: 'Why Proper Sport Gear Storage Matters'
      }
    }
  ]
}

export const typesOfSportsSectionContent = {
  title: 'Types of Sports Gear We Store',
  description:
    'At ShipAndStorage, we cater to a wide range of sports enthusiasts by offering storage solutions for various equipment, including:',

  articles: [
    {
      content: [
        {
          id: 1,
          title: 'Winter Sports Gear',
          points: ['Skis, snowboards, and boots.', 'Ice hockey sticks and gear.']
        },
        {
          id: 2,
          title: 'Water Sports Equipment',
          points: ['Surfboards, paddleboards, and kayaks.', 'Diving gear and wetsuits.']
        },
        {
          id: 3,
          title: 'Outdoor Sports Gear',
          points: ['Camping tents, hiking gear, and fishing equipment.', 'Bikes and climbing equipment.']
        },
        {
          id: 4,
          title: 'Specialized Sports Items',
          points: ['Golf clubs, tennis rackets, and archery equipment.', 'Lacrosse sticks and cricket gear.']
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/types-of-sports-gear-we-store.jpg',
        alt: 'Types of Sports Gear We Store'
      }
    }
  ]
}

export const howServiceWorksSectionContent = {
  title: 'Why Choose ShipAndStorage for Your Sport Gear Storage?',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Choose Our Storage' },

  image: {
    src: '/images/v2/pages/main-pages/sport-gear-storage/why-choose-shipandstorage-for-your-sport-gear-storage.svg',
    alt: 'Why Choose ShipAndStorage for Your Sport Gear Storage?'
  },

  cards: [
    {
      id: 1,
      title: 'Flexible Storage Options',
      points: [
        'Pay per item stored, ensuring you only pay for what you need.',
        'Short-term and long-term storage plans with no hidden fees.'
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/flexible-storage-options.svg',
        alt: 'Flexible Storage Options'
      }
    },
    {
      id: 2,
      title: 'Secure Facilities',
      points: [
        'Climate-controlled environments to prevent damage from temperature and humidity changes.',
        '24/7 monitoring to keep your gear safe.'
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/secure-facilities.svg',
        alt: 'Secure Facilities'
      }
    },
    {
      id: 3,
      title: 'Convenient Accessibility',
      points: [
        'Easy retrieval options to fit your schedule.',
        'Shipping services available to deliver your gear directly to your destination.'
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/convenient-accessibility.svg',
        alt: 'Convenient Accessibility'
      }
    },
    {
      id: 4,
      title: 'Expert Support',
      points: [
        'Personalized assistance for oversized or delicate sports equipment.',
        'Packing tips and custom storage solutions for unique needs.'
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/expert-support.svg',
        alt: 'Expert Support'
      }
    }
  ]
}

export const prohibitedItemsSectionContent = {
  title: 'Prohibited Items for Sport Gear Storage',
  description: 'To ensure safety and compliance, avoid storing the following items:',

  articles: [
    {
      content: [
        {
          id: 1,
          points: [
            'Flammable or hazardous materials, such as fuel or cleaning chemicals.',
            'Perishable items or food stored with your gear.',
            'Unregistered or illegal equipment.',
            'If you have questions about whether your items can be stored, contact our team for clarification.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/sport-gear-storage/prohibited-items-for-sport-gear-storage.jpg',
        alt: 'Prohibited Items for Sport Gear Storage'
      }
    }
  ]
}

export const promotionalBannerSectionContent = {
  id: 1,
  title: 'Convenient Sport Gear Storage Solutions',
  description:
    'Store your sport gear with us for convenience. Ship your golf clubs, ski gear, or snowboard to our secure facility and save space at home.',
  image: {
    src: '/images/v2/pages/main-pages/sport-gear-storage/promotional-banner-image.svg',
    alt: 'Convenient Sport Gear Storage Solutions'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.MOVING_BOX_STORAGE.href, text: 'Store With Us' }
}

export const faqSectionContent = {
  title: 'FAQs About Sports Gear Storage',

  questions: [
    {
      id: 1,
      question: 'What types of sports gear can I store?',
      answer: 'We accommodate a wide range of gear, including skis, bikes, golf clubs, surfboards, and more.'
    },
    {
      id: 2,
      question: 'Is my equipment safe in storage?',
      answer:
        'Absolutely. Our facilities feature 24/7 monitoring, restricted access, and climate control for ultimate safety.'
    },
    {
      id: 3,
      question: 'Can I access my equipment anytime?',
      answer: 'Yes! With our flexible retrieval options, you can request your gear whenever you need it.'
    },
    {
      id: 4,
      question: 'Do you offer pickup and delivery?',
      answer: 'Yes, we provide convenient pickup and delivery services to make storage hassle-free.'
    },
    {
      id: 5,
      question: 'How much does sports gear storage cost?',
      answer:
        'Costs depend on the type and volume of gear stored, as well as the duration. Contact us for a personalized quote.'
    }
  ]
}
